import { useState } from 'react'
import { KeyRound, Pencil, Search } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { primaryButton, quietButton, secondaryButton, small } from '@/components/shell/StatusScreens'
import { Drawer } from '@/components/ui/Drawer'
import { toast } from '@/components/ui/Toaster'
import { ApiError, request } from '@/lib/api'
import { cn } from '@/lib/utils'
import { EquipePage } from '../../../admin/equipe/EquipePage'
import { ADMIN_ROLES, ROLE_LABELS, type AdminRole } from '../../../admin/shared/permissions'
import { Card, Chip, Segmented } from '../../../admin/shared/ui'
import type { AccessLink, PanelUser, UserInput } from '../../contract'
import { FRENTES, ROLE_FRENTES } from '../../frentes'
import { CONFIG_GROUP, fieldInput, fmtDateTime, Switch } from '../ui'

/* Configurações › Pessoas › Usuários e acessos: os usuários do panel (tabela panel_users — quem entra e com qual perfil;
   GET/POST /admin/users, só admin), a frente que cada perfil abre e, abaixo, a tela Equipe e permissões do Staff. */
export function UsersSection() {
    return <EquipePage group={CONFIG_GROUP} intro={<><PanelUsersCard /><RoleFrentesCard /></>} />
}

const usersKey = ['panel', 'admin', 'users'] as const
const EMPTY: UserInput = { email: '', name: '', role: 'leitura', title: null, department: null, active: true }

type UsersFilter = 'todos' | 'sem-senha' | 'admins' | 'desativados'

const matchesFilter = (u: PanelUser, f: UsersFilter) =>
    f === 'todos' ? true : f === 'sem-senha' ? u.active && !u.passwordSetAt : f === 'admins' ? u.role === 'admin' : !u.active

const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const initials = (name: string) => name.split(/\s+/).filter(w => /^[A-ZÀ-Ú]/.test(w)).slice(0, 2).map(w => w[0]).join('') || '?'
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })

function PanelUsersCard() {
    const query = useQuery({ queryKey: usersKey, queryFn: () => request<PanelUser[]>('GET', '/admin/users') })
    const [editing, setEditing] = useState<{ user: UserInput; isNew: boolean } | null>(null)
    const [linkFor, setLinkFor] = useState<PanelUser | null>(null)
    const [filter, setFilter] = useState<UsersFilter>('todos')
    const [q, setQ] = useState('')
    const users = query.data ?? []
    const count = (f: UsersFilter) => users.filter(u => matchesFilter(u, f)).length
    const shown = users.filter(u => matchesFilter(u, filter) && (!q.trim() || fold(`${u.name} ${u.email} ${u.title ?? ''} ${u.department ?? ''}`).includes(fold(q.trim()))))
    return (
        <Card
            id="cfg-panel-users"
            title="Usuários do panel"
            aside={<button type="button" className={cn(primaryButton, small)} onClick={() => setEditing({ user: EMPTY, isNew: true })}>Incluir pessoa</button>}
        >
            <p className="m-0 max-w-[72ch] text-[13px] text-muted">Quem está ativo aqui entra no painel com o próprio e-mail e senha; o perfil define a frente. Quem não está na lista entra só para ler. Só admin vê as Configurações.</p>
            <div className="flex flex-wrap items-center gap-2.5">
                <Segmented
                    label="Mostrar"
                    value={filter}
                    onChange={setFilter}
                    options={[
                        { id: 'todos', label: 'Todos', count: count('todos') },
                        { id: 'sem-senha', label: 'Sem senha', count: count('sem-senha') },
                        { id: 'admins', label: 'Admins', count: count('admins') },
                        { id: 'desativados', label: 'Desativados', count: count('desativados') },
                    ]}
                />
                <label className="flex min-h-9 min-w-[200px] flex-[1_1_220px] items-center gap-2 rounded-[10px] border border-line-2 bg-panel px-2.5 text-muted">
                    <Search size={15} aria-hidden="true" />
                    <span className="sr-only">Buscar pessoa</span>
                    <input id="usr-search" type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Nome, e-mail ou cargo" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-[14px] text-fg outline-none" />
                </label>
            </div>
            {query.isError && <p role="alert" className="m-0 text-[13px] text-[var(--red-ink)]">Não deu para carregar os usuários.</p>}
            <ul className="m-0 flex list-none flex-col overflow-hidden rounded-[12px] border border-line p-0">
                {query.isPending && <li className="px-4 py-6 text-center text-[13.5px] text-muted">Carregando…</li>}
                {!query.isPending && shown.length === 0 && <li className="px-4 py-6 text-center text-[13.5px] text-muted">Ninguém com esse filtro.</li>}
                {shown.map(u => <UserRow key={u.email} u={u} onLink={() => setLinkFor(u)} onEdit={() => setEditing({ user: u, isNew: false })} />)}
            </ul>
            <UserDrawer editing={editing} onClose={() => setEditing(null)} />
            <AccessLinkDrawer user={linkFor} onClose={() => setLinkFor(null)} />
        </Card>
    )
}

/** Uma pessoa: quem é, em que frente entra, como está a senha e as ações. No celular vira um cartão. */
function UserRow({ u, onLink, onEdit }: { u: PanelUser; onLink: () => void; onEdit: () => void }) {
    const admin = u.role === 'admin'
    const frente = FRENTES[ROLE_FRENTES[u.role][0]!].label
    const roleLabel = ROLE_LABELS[u.role]
    const job = [u.title, u.department].filter(Boolean).join(' · ')
    const linkPending = !u.passwordSetAt && u.accessLinkExpiresAt && new Date(u.accessLinkExpiresAt).getTime() > Date.now()
    return (
        <li className={cn('grid items-center gap-x-5 gap-y-2.5 border-t border-line px-4 py-3 first:border-t-0 hover:bg-[var(--hover)] md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,0.9fr)_auto]', !u.active && 'opacity-60')}>
            <div className="flex min-w-0 items-center gap-3">
                <span aria-hidden="true" className="grid size-10 flex-none place-items-center rounded-full text-[13.5px] font-semibold" style={admin ? { background: 'var(--green)', color: 'var(--on-green)' } : { background: 'var(--tint)', color: 'var(--green)' }}>{initials(u.name)}</span>
                <div className="min-w-0">
                    <span className="block truncate text-[14.5px] font-semibold" title={u.name}>{u.name}</span>
                    <span className="block truncate text-[12.5px] text-muted" title={u.email}>{u.email}</span>
                    {job && <span className="block truncate text-[12px] text-dim" title={job}>{job}</span>}
                </div>
            </div>
            <div className="flex min-w-0 flex-col items-start gap-1">
                {admin ? <Chip tone="green" dot>Admin · Configurações</Chip> : <span className="truncate text-[13.5px] font-medium">{frente}</span>}
                <span className="truncate text-[12px] text-muted">{admin ? 'abre todas as frentes' : roleLabel === frente ? 'perfil e frente' : `perfil ${roleLabel}`}</span>
            </div>
            <div className="flex min-w-0 flex-col items-start gap-1">
                {!u.active
                    ? <Chip tone="neutral">Desativado</Chip>
                    : u.passwordSetAt
                        ? <Chip tone="ok" dot>Senha criada</Chip>
                        : linkPending ? <Chip tone="info" dot>Link enviado</Chip> : <Chip tone="warn" dot>Sem senha</Chip>}
                <span className="text-[12px] text-muted">
                    {!u.active ? `desde ${shortDate(u.updatedAt)}` : u.passwordSetAt ? `em ${shortDate(u.passwordSetAt)}` : linkPending ? `vale até ${shortDate(u.accessLinkExpiresAt!)}` : 'gere um link de acesso'}
                </span>
            </div>
            <div className="flex items-center gap-1.5 md:justify-end">
                {u.active && (
                    <button type="button" className={cn(u.passwordSetAt ? secondaryButton : primaryButton, small, 'whitespace-nowrap')} onClick={onLink}>
                        <KeyRound size={14} aria-hidden="true" />
                        {u.passwordSetAt ? 'Trocar senha' : 'Link de acesso'}
                    </button>
                )}
                <button type="button" className={cn(quietButton, small, 'whitespace-nowrap')} onClick={onEdit} aria-label={`Editar ${u.name}`}>
                    <Pencil size={14} aria-hidden="true" />
                    Editar
                </button>
            </div>
        </li>
    )
}

function UserDrawer({ editing, onClose }: { editing: { user: UserInput; isNew: boolean } | null; onClose: () => void }) {
    const client = useQueryClient()
    const [form, setForm] = useState<UserInput>(EMPTY)
    const [openFor, setOpenFor] = useState<string | null>(null)
    const key = editing ? `${editing.isNew}:${editing.user.email}` : null
    if (key !== openFor) {
        setOpenFor(key)
        if (editing) setForm(editing.user)
    }
    const save = useMutation({
        mutationFn: (body: UserInput) => request<PanelUser>('POST', '/admin/users', body),
        onSuccess: u => {
            client.invalidateQueries({ queryKey: usersKey })
            toast({ message: `${u.name}: salvo. Perfil ${ROLE_LABELS[u.role]}${u.active ? '' : ' · desativado'}.` })
            onClose()
        },
        onError: e => toast({ message: e instanceof ApiError && e.code === 'conflict' ? 'O panel precisa de pelo menos um admin ativo.' : 'Não deu para salvar. Confira e-mail e nome.', tone: 'err' }),
    })
    const set = <K extends keyof UserInput>(k: K, v: UserInput[K]) => setForm(f => ({ ...f, [k]: v }))
    const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) && form.name.trim().length >= 2
    return (
        <Drawer
            open={!!editing}
            onClose={onClose}
            title={editing?.isNew ? 'Incluir pessoa' : 'Editar pessoa'}
            subtitle={editing?.isNew ? undefined : editing?.user.email}
            footer={(
                <>
                    <button type="button" className={cn(quietButton, small)} onClick={onClose}>Cancelar</button>
                    <button type="button" className={cn(primaryButton, small)} disabled={!valid || save.isPending} onClick={() => save.mutate({ ...form, email: form.email.trim().toLowerCase(), name: form.name.trim() })}>Salvar</button>
                </>
            )}
        >
            <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">E-mail
                    <input id="usr-email" type="email" className={fieldInput} value={form.email} disabled={!editing?.isNew} onChange={e => set('email', e.target.value)} placeholder="nome@childfund.org" />
                </label>
                <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">Nome
                    <input id="usr-name" className={fieldInput} value={form.name} onChange={e => set('name', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">Cargo
                    <input id="usr-title" className={fieldInput} value={form.title ?? ''} onChange={e => set('title', e.target.value || null)} />
                </label>
                <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">Departamento
                    <input id="usr-dept" className={fieldInput} value={form.department ?? ''} onChange={e => set('department', e.target.value || null)} />
                </label>
                <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">Perfil
                    <select id="usr-role" className={fieldInput} value={form.role} onChange={e => set('role', e.target.value as AdminRole)}>
                        {ADMIN_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]} → {FRENTES[ROLE_FRENTES[r][0]!].label}</option>)}
                    </select>
                </label>
                {form.role === 'admin' && <p className="m-0 text-[12.5px] text-[var(--yellow-ink)]">Admin vê as Configurações gerais (usuários, frentes, agentes, integrações).</p>}
                <Switch checked={form.active} onChange={on => set('active', on)} label="Ativo (pode entrar no panel)" />
            </div>
        </Drawer>
    )
}

function RoleFrentesCard() {
    return (
        <Card id="cfg-role-frentes" title="Frente de cada perfil" aside="a primeira é a que abre ao entrar">
            <ul className="m-0 grid list-none gap-x-6 gap-y-2 p-0" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                {ADMIN_ROLES.map(role => {
                    const [first, ...rest] = ROLE_FRENTES[role]
                    return (
                        <li key={role} className="flex min-w-0 flex-wrap items-center gap-1.5 text-[13px]">
                            <span className="font-medium">{ROLE_LABELS[role]}</span>
                            <span aria-hidden="true" className="text-muted">→</span>
                            {first && <Chip tone="green">{FRENTES[first].label}</Chip>}
                            {rest.length > 0 && <span className="text-muted">+ {rest.length === Object.keys(FRENTES).length - 1 ? 'todas as outras' : rest.map(f => FRENTES[f].label).join(', ')}</span>}
                        </li>
                    )
                })}
            </ul>
        </Card>
    )
}

/** Gera o link de uso único (72 h) para a pessoa criar ou trocar a senha; o admin copia e manda (WhatsApp, Teams…). */
function AccessLinkDrawer({ user, onClose }: { user: PanelUser | null; onClose: () => void }) {
    const client = useQueryClient()
    const [link, setLink] = useState<AccessLink | null>(null)
    const [openFor, setOpenFor] = useState<string | null>(null)
    if ((user?.email ?? null) !== openFor) {
        setOpenFor(user?.email ?? null)
        setLink(null)
    }
    const create = useMutation({
        mutationFn: (email: string) => request<AccessLink>('POST', '/admin/users/access-link', { email }),
        onSuccess: l => {
            setLink(l)
            client.invalidateQueries({ queryKey: usersKey })
        },
        onError: () => toast({ message: 'Não deu para gerar o link. Tente de novo.', tone: 'err' }),
    })
    const copy = async () => {
        if (!link) return
        try {
            await navigator.clipboard.writeText(link.url)
            toast({ message: 'Link copiado.' })
        } catch {
            toast({ message: 'Não deu para copiar: selecione o link e copie.', tone: 'err' })
        }
    }
    const first = user?.name.split(' ')[0] ?? ''
    return (
        <Drawer
            open={!!user}
            onClose={onClose}
            title={user?.passwordSetAt ? 'Link para trocar a senha' : 'Link de acesso'}
            subtitle={user ? `${user.name} · ${user.email}` : undefined}
            footer={<button type="button" className={cn(quietButton, small)} onClick={onClose}>Fechar</button>}
        >
            <div className="flex flex-col gap-3 text-[13.5px]">
                <p className="m-0 text-muted">{first} abre o link, cria a própria senha e já entra no painel. Vale <b className="text-fg">uma vez só, por 72 horas</b>; gerar outro cancela o anterior. A senha nunca passa por você nem fica visível para ninguém.</p>
                {!link ? (
                    <button type="button" className={cn(primaryButton, small, 'self-start')} disabled={!user || create.isPending} onClick={() => user && create.mutate(user.email)}>
                        {create.isPending ? 'Gerando…' : 'Gerar link'}
                    </button>
                ) : (
                    <>
                        <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">Link
                            <input id="usr-link" readOnly value={link.url} onFocus={e => e.currentTarget.select()} className={fieldInput} />
                        </label>
                        <span className="text-[12.5px] text-muted">Vale até {fmtDateTime(link.expiresAt)}.</span>
                        <button type="button" className={cn(primaryButton, small, 'self-start')} onClick={() => void copy()}>Copiar link</button>
                        <p className="m-0 text-[12.5px] text-muted">Mande só para a própria pessoa (WhatsApp ou Teams). Quem tiver o link cria a senha da conta.</p>
                    </>
                )}
            </div>
        </Drawer>
    )
}
